import { formatRupiah, uuidv7 } from '@safepos/shared';
import { Alert, Badge, Button, Modal, SelectField, TextField } from '@safepos/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useSubmit } from '../lib/useForm';

/** Baris stok yang dibutuhkan dialog (subset dari daftar stok). */
export interface RepackRow {
  variantId: string;
  name: string;
  unit: string;
  qty: number;
  avgCost: number;
  contentQty: number | null;
  price?: number;
}

interface Line {
  key: number;
  variantId: string;
  qty: string;
  content: string;
}

const num = (s: string) => Number(s.replace(',', '.'));
const fmt = (q: number) => new Intl.NumberFormat('id-ID', { maximumFractionDigits: 3 }).format(q);
const valid = (s: string) => s.trim() !== '' && Number.isFinite(num(s)) && num(s) > 0;

export interface RepackRecord {
  id: string;
  at: string;
  note: string | null;
  weighting: 'content' | 'price';
  costTotal: number;
  lossContent: number | null;
  source: { name: string; qty: number; content: number | null };
  outputs: { name: string; qty: number; content: number | null; unitCost: number }[];
}

/**
 * Pecah stok: pilih barang sumber (mis. karung gula) dan jumlah yang dipecah, lalu daftar barang hasil beserta jumlahnya.
 * Isi per unit (mis. 25 kg per karung, 1 kg, 0,5 kg, 0,25 kg) membuat sistem menghitung susut dan membagi modal secara adil;
 * isi yang diketik diingat untuk pecah stok berikutnya.
 */
export function RepackDialog({
  outletId,
  rows,
  sourceId,
  onClose,
  onDone,
}: {
  outletId: string;
  rows: RepackRow[];
  sourceId?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const byId = useMemo(() => new Map(rows.map((r) => [r.variantId, r])), [rows]);
  const [id] = useState(() => uuidv7()); // tetap sama saat mencoba ulang: pengiriman ulang tidak menggandakan
  const init = sourceId ? byId.get(sourceId) : undefined;
  const [src, setSrc] = useState({
    variantId: sourceId ?? '',
    qty: '1',
    content: init?.contentQty != null ? String(init.contentQty) : '',
  });
  const [outs, setOuts] = useState<Line[]>([{ key: 1, variantId: '', qty: '', content: '' }]);
  const [note, setNote] = useState('');
  const [seq, setSeq] = useState(2);

  const source = byId.get(src.variantId);
  const pickSource = (variantId: string) => {
    const r = byId.get(variantId);
    setSrc({ variantId, qty: src.qty, content: r?.contentQty != null ? String(r.contentQty) : '' });
  };
  const pickOut = (key: number, variantId: string) => {
    const r = byId.get(variantId);
    setOuts(
      outs.map((o) =>
        o.key === key
          ? { ...o, variantId, content: r?.contentQty != null ? String(r.contentQty) : '' }
          : o,
      ),
    );
  };
  const patchOut = (key: number, p: Partial<Line>) =>
    setOuts(outs.map((o) => (o.key === key ? { ...o, ...p } : o)));

  // ---- ringkasan (rumus yang sama dengan server; server tetap penentu) ----
  const srcQty = valid(src.qty) ? num(src.qty) : 0;
  const filled = outs.filter((o) => o.variantId && valid(o.qty));
  const contentsKnown =
    valid(src.content) && filled.length > 0 && filled.every((o) => valid(o.content));
  const srcTotal = contentsKnown ? srcQty * num(src.content) : 0;
  const outTotal = contentsKnown ? filled.reduce((s, o) => s + num(o.qty) * num(o.content), 0) : 0;
  const exceeds = contentsKnown && outTotal > srcTotal + 1e-6;
  const loss = contentsKnown ? srcTotal - outTotal : null;
  const costTotal = source ? Math.round(srcQty * source.avgCost) : 0;
  const weights = filled.map((o) =>
    contentsKnown ? num(o.qty) * num(o.content) : num(o.qty) * (byId.get(o.variantId)?.price ?? 0),
  );
  const wSum = weights.reduce((s, x) => s + x, 0);
  const unitCost = (i: number) =>
    wSum > 0 && filled[i]
      ? Math.round((costTotal * (weights[i]! / wSum)) / num(filled[i]!.qty))
      : null;
  const noStock = !!source && srcQty > source.qty;
  const dup =
    new Set([src.variantId, ...filled.map((o) => o.variantId)]).size !== filled.length + 1;
  const ready = !!source && srcQty > 0 && filled.length > 0 && !exceeds && !noStock && !dup;

  const save = useSubmit(async () => {
    const body = {
      id,
      outletId,
      source: {
        variantId: src.variantId,
        qty: num(src.qty),
        ...(valid(src.content) ? { content: num(src.content) } : {}),
      },
      outputs: filled.map((o) => ({
        variantId: o.variantId,
        qty: num(o.qty),
        ...(valid(o.content) ? { content: num(o.content) } : {}),
      })),
      note: note.trim() || null,
    };
    await api.post('/stock-repacks', body);
    onDone();
  });

  const pool = rows.filter((r) => r.variantId !== src.variantId);
  return (
    <Modal open title="Pecah stok" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save.run();
        }}
      >
        {save.error && <Alert>{save.error}</Alert>}
        <p className="text-sm text-slate-600">
          Pecah satu barang (mis. karung gula) menjadi beberapa kemasan kecil. Stok barang sumber
          berkurang, stok barang hasil bertambah, dan modalnya ikut berpindah.
        </p>

        <fieldset className="space-y-3 rounded-lg border border-slate-200 p-3">
          <legend className="px-1 text-sm font-medium">Barang yang dipecah</legend>
          <SelectField
            label="Barang sumber"
            value={src.variantId}
            onChange={(e) => pickSource(e.target.value)}
          >
            <option value="">Pilih barang…</option>
            {rows
              .filter((r) => r.qty > 0)
              .map((r) => (
                <option key={r.variantId} value={r.variantId}>
                  {r.name} (stok {fmt(r.qty)} {r.unit})
                </option>
              ))}
          </SelectField>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label={`Jumlah dipecah${source ? ` (${source.unit})` : ''}`}
              inputMode="decimal"
              value={src.qty}
              onChange={(e) => setSrc({ ...src, qty: e.target.value })}
              error={noStock ? `Stok hanya ${fmt(source!.qty)}` : undefined}
              required
            />
            <TextField
              label="Isi per unit (mis. 25 kg per karung)"
              inputMode="decimal"
              hint="Opsional; diingat untuk berikutnya"
              value={src.content}
              onChange={(e) => setSrc({ ...src, content: e.target.value })}
            />
          </div>
        </fieldset>

        <fieldset className="space-y-3 rounded-lg border border-slate-200 p-3">
          <legend className="px-1 text-sm font-medium">Menjadi</legend>
          {outs.map((o, i) => (
            <div key={o.key} className="space-y-2 rounded-lg bg-slate-50 p-3">
              <SelectField
                label={`Barang hasil ${i + 1}`}
                value={o.variantId}
                onChange={(e) => pickOut(o.key, e.target.value)}
              >
                <option value="">Pilih barang…</option>
                {pool
                  .filter(
                    (r) =>
                      r.variantId === o.variantId || !outs.some((x) => x.variantId === r.variantId),
                  )
                  .map((r) => (
                    <option key={r.variantId} value={r.variantId}>
                      {r.name}
                    </option>
                  ))}
              </SelectField>
              <div className="grid gap-3 sm:grid-cols-2">
                <TextField
                  label="Jumlah hasil (unit)"
                  inputMode="decimal"
                  value={o.qty}
                  onChange={(e) => patchOut(o.key, { qty: e.target.value })}
                />
                <TextField
                  label="Isi per unit (mis. 0,5 kg)"
                  inputMode="decimal"
                  value={o.content}
                  onChange={(e) => patchOut(o.key, { content: e.target.value })}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>
                  {filled.indexOf(o) >= 0 && unitCost(filled.indexOf(o)) != null
                    ? `Modal per unit ≈ ${formatRupiah(unitCost(filled.indexOf(o))!)}`
                    : ' '}
                </span>
                {outs.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setOuts(outs.filter((x) => x.key !== o.key))}
                  >
                    Hapus
                  </Button>
                )}
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setOuts([...outs, { key: seq, variantId: '', qty: '', content: '' }]);
              setSeq(seq + 1);
            }}
          >
            + Tambah barang hasil
          </Button>
        </fieldset>

        {exceeds && (
          <Alert>
            Isi hasil ({fmt(outTotal)}) melebihi isi yang dipecah ({fmt(srcTotal)}). Kurangi jumlah
            hasil.
          </Alert>
        )}
        {dup && <Alert>Barang sumber dan barang hasil tidak boleh sama atau ganda.</Alert>}
        {ready && contentsKnown && loss != null && (
          <Alert tone={loss > 1e-6 ? 'info' : 'success'}>
            Isi dipecah <b>{fmt(srcTotal)}</b> → isi hasil <b>{fmt(outTotal)}</b>
            {loss > 1e-6 ? (
              <>
                {' '}
                · <Badge tone="warning">susut {fmt(loss)}</Badge>
              </>
            ) : (
              ' · tidak ada susut'
            )}
            . Modal {formatRupiah(costTotal)} dibagi menurut isi.
          </Alert>
        )}
        {ready && !contentsKnown && (
          <Alert tone="info">
            Isi per unit belum lengkap, jadi susut tidak dihitung dan modal{' '}
            {formatRupiah(costTotal)} dibagi menurut harga jual. Isi kolom “Isi per unit” untuk
            pembagian yang akurat.
          </Alert>
        )}

        <TextField
          label="Catatan (opsional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={200}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button type="submit" disabled={!ready || save.busy}>
            {save.busy ? 'Memecah…' : 'Pecah stok'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Riwayat pecah stok terbaru (outlet terpilih). */
export function useRepackHistory(outletId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['stock-repacks', outletId],
    enabled: enabled && !!outletId,
    queryFn: () => api.get<RepackRecord[]>(`/stock-repacks?outletId=${outletId}&limit=10`),
  });
}
